import React from 'react'
import {useIntl} from 'react-intl'

import {
    Box,
    RadioGroup,
    Radio,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Tooltip} from '@chakra-ui/react'
import StoreSelectorModal from '../store-selector-modal'
import StoreDetails from '../store-details'
import {fonts} from '../../theme/foundations/fonts'
import {pxToRem} from '../../utills/pxToRem'
import useUpdateShopperContext from '../store-selector-modal/updateShopperContext'
import {storeShipmentButtonStyles, shipToMeTooltipStyle} from './index.styles'
import PropTypes from 'prop-types'
import {useState, useEffect} from 'react'
import {statesList} from '../../constants'
import {useSearchStores} from '@salesforce/commerce-sdk-react'
import useSitePreference from '../../hooks/use-site-preference'

const DeliveryAddress = ({
    product,
    quantity,
    updateDeliceryType,
    storePickUp,
    setStorePickUp,
    marginTop,
    setQuantity,
    minOrderQuantity
}) => {
    const intl = useIntl()
    const {shopperContext} = useUpdateShopperContext()

    const {
        isOpen: isOpenStoreSelector,
        onOpen: onOpenStoreSelector,
        onClose: onCloseStoreSelector
    } = useDisclosure()

    const handleDeliveryAddressChange = (e) => {
        setStorePickUp(e), updateDeliceryType && updateDeliceryType(e)
        setQuantity && setQuantity(minOrderQuantity)
    }

    const [statesWithStores, setStatesWithStores] = useState([])
    const {data} = useSitePreference('statesMapping,storeLookupMaxDistance')
    const statesList = data?.statesMapping
    const {data: defaultLocationData} = useSitePreference('defaultStoreLatLong')
    const [defaultLatLng, setDefaultLatLng] = useState({lat: null, lng: null})

    useEffect(() => {
        if (defaultLocationData?.defaultStoreLatLong) {
            try {
                const parsed = JSON.parse(defaultLocationData.defaultStoreLatLong)
                setDefaultLatLng({lat: parsed.lat, lng: parsed.lng})
            } catch (err) {
                console.error('Invalid defaultStoreLatLong format', err)
            }
        }
    }, [defaultLocationData])

    const [parameters, setParameters] = useState(null)
    const [enableSearchStore, setEnableSearchStore] = useState(false)

    useEffect(() => {
        if (defaultLatLng.lat != null && defaultLatLng.lng != null) {
            setParameters({
                countryCode: 'US',
                latitude: defaultLatLng.lat,
                longitude: defaultLatLng.lng,
                distanceUnit: 'mi',
                limit: 200
            })
            setEnableSearchStore(true)
        }
    }, [defaultLatLng])

    const {data: storeData} = useSearchStores(
        {
            parameters: parameters
        },
        {
            enabled: enableSearchStore
        }
    )

    useEffect(() => {
        if (storeData?.data && statesList) {
            const storeStatesSet = new Set(storeData.data.map((store) => store.stateCode))
            const filteredStates = Object.entries(statesList)
                .filter(([code]) => storeStatesSet.has(code))
                .map(([code, name]) => ({code, name}))
            setStatesWithStores(filteredStates)
        }
    }, [storeData, statesList])

    return (
        <>
            <RadioGroup
                variant="outline"
                value={storePickUp}
                sx={storeShipmentButtonStyles}
                onChange={(e) => handleDeliveryAddressChange(e)}
            >
                <Box className="radio" marginTop={marginTop}>
                    {product?.c_shipToHomeOnPWA?.showShipToHome && (
                        <Radio
                            value="shipToMe"
                            isDisabled={!product.c_shipToHomeOnPWA.enableShipToHome}
                        >
                            {intl.formatMessage({
                                id: 'product_list.store.shiptome',
                                defaultMessage: 'Ship to Me'
                            })}
                        </Radio>
                    )}
                    {product?.c_instorePickupOnPWA?.showInStorePickup && (
                        <Tooltip
                            isDisabled={product.c_instorePickupOnPWA.enableInStorePickup}
                            label={intl.formatMessage({
                                id: 'product_list.store.pickupinstore.toolip',
                                defaultMessage: 'Select a size to see availability'
                            })}
                            sx={shipToMeTooltipStyle}
                        >
                            <Box>
                                <Radio
                                    value="pickUpInStore"
                                    isDisabled={!product.c_instorePickupOnPWA.enableInStorePickup}
                                >
                                    {intl.formatMessage({
                                        id: 'product_list.store.pickupinstore',
                                        defaultMessage: 'Pick Up in Store'
                                    })}
                                </Radio>
                            </Box>
                        </Tooltip>
                    )}
                </Box>
            </RadioGroup>

            {/* my store */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: '16px',
                    marginTop: pxToRem(16)
                }}
            >
                {/* selected store details */}
                <StoreDetails />
                {/* change store button */}
                <Box
                    as="button"
                    color="cav_calltoaction_darkred"
                    fontSize={{base: pxToRem(10), md: pxToRem(12)}}
                    fontFamily={fonts.primary}
                    textDecoration={'underline'}
                    textTransform={'uppercase'}
                    fontWeight={500}
                    lineHeight="normal"
                    letterSpacing={pxToRem(1)}
                    _hover={{
                        textDecoration: 'none'
                    }}
                    onClick={() => onOpenStoreSelector()}
                >
                    {shopperContext &&
                    Object.keys(shopperContext.customQualifiers?.storeDetails || {})?.length
                        ? intl.formatMessage({
                              id: 'product_list.store.change.label',
                              defaultMessage: 'Change'
                          })
                        : intl.formatMessage({
                              id: 'product_list.store.selectSore.label',
                              defaultMessage: 'Select a store'
                          })}
                </Box>
            </Box>

            {/* store selector modal */}
            <StoreSelectorModal
                isOpen={isOpenStoreSelector}
                onClose={onCloseStoreSelector}
                isPdp={true}
                quantity={quantity}
                masterProductId={product?.master?.masterId}
                productId={product?.id}
                isMaster={Boolean(product?.type?.master)}
                states={statesWithStores}
            />
        </>
    )
}

DeliveryAddress.propTypes = {
    product: PropTypes.object.isRequired,
    quantity: PropTypes.number.isRequired,
    updateDeliceryType: PropTypes.func,
    storePickUp: PropTypes.string.isRequired,
    setStorePickUp: PropTypes.func.isRequired,
    marginTop: PropTypes.string,
    setQuantity: PropTypes.func,
    minOrderQuantity: PropTypes.number.isRequired
}

export default DeliveryAddress
